import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  getConsoleAlarms,
  getConsoleBusinesses,
  getConsoleReadout,
  type ConsoleBusiness,
  type ConsoleReadout,
} from '../lib/api'
import { loadReason, unacknowledgedCount } from '../lib/consoleStatus'
import { BusinessControls } from '../components/console/BusinessControls'
import { ConsoleShell, SectionMessage } from '../components/console/ConsoleShell'
import { card, h2Class, kicker, linkClass } from '../components/soc/SocShell'

// One business's readout on its own page (BRIEF-F1b; F1a item 3): today's
// daily_readout text exactly as the route returns it, unchanged. Above it
// (BRIEF-F2), the business's daily cap and close watch, which staff may
// change here after a confirmation step.

export function ConsoleReadoutPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>()
  const [readout, setReadout] = useState<ConsoleReadout | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [alarmCount, setAlarmCount] = useState<number | null>(null)
  const [business, setBusiness] = useState<ConsoleBusiness | null | undefined>(undefined)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  const loadBusiness = useCallback(() => {
    let cancelled = false
    setSettingsError(null)
    getConsoleBusinesses()
      .then((all) => !cancelled && setBusiness(all.find((b) => b.tenant_id === tenantId) ?? null))
      .catch((err) => !cancelled && setSettingsError(loadReason(err)))
    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    setBusiness(undefined)
    return loadBusiness()
  }, [loadBusiness])

  useEffect(() => {
    let cancelled = false
    setReadout(null)
    setError(null)
    getConsoleReadout(tenantId)
      .then((r) => !cancelled && setReadout(r))
      .catch((err) => !cancelled && setError(loadReason(err)))
    getConsoleAlarms()
      .then((a) => !cancelled && setAlarmCount(unacknowledgedCount(a)))
      .catch(() => !cancelled && setAlarmCount(null))
    return () => {
      cancelled = true
    }
  }, [tenantId])

  return (
    <ConsoleShell alarmCount={alarmCount}>
      <Link to="/console" className={linkClass}>
        ← Back to the console
      </Link>
      {settingsError !== null ? (
        <SectionMessage tone="error">Could not load the settings: {settingsError}</SectionMessage>
      ) : business === undefined ? (
        <SectionMessage>Loading the settings…</SectionMessage>
      ) : business === null ? (
        <SectionMessage>This business has no shift settings to change.</SectionMessage>
      ) : (
        <BusinessControls b={business} onChanged={loadBusiness} />
      )}
      <section aria-label="Readout" className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className={kicker}>The readout this business is sent</div>
            <h2 className={h2Class}>Readout</h2>
          </div>
          {/* BRIEF-F3: their own page as the owner sees it, read-only. */}
          <Link
            to={`/console/business/${encodeURIComponent(tenantId)}/as-owner`}
            className={linkClass}
            data-testid="see-their-page"
          >
            See their page
          </Link>
        </div>
        {error !== null ? (
          <SectionMessage tone="error">Could not load: {error}</SectionMessage>
        ) : readout === null ? (
          <SectionMessage>Loading the readout…</SectionMessage>
        ) : !readout.readout.trim() ? (
          <SectionMessage>The readout for this business is empty.</SectionMessage>
        ) : (
          <pre
            data-testid="readout-text"
            className={`${card} m-0 whitespace-pre-wrap break-words px-5 py-4 font-sans text-[15px] leading-relaxed`}
          >
            {readout.readout}
          </pre>
        )}
      </section>
    </ConsoleShell>
  )
}
